import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import { POSTS } from "../data/blogPosts";
import { ArrowRight, Clock, Sparkle } from "@phosphor-icons/react";

export default function BlogPage() {
  return (
    <div className="min-h-screen relative noise-bg" data-testid="blog-page">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/services" className="text-neutral-400 hover:text-white transition-colors">Services</Link>
            <Link to="/pricing" className="text-neutral-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/blog" className="text-white font-medium">Blog</Link>
            <Link to="/contact" className="text-neutral-400 hover:text-white transition-colors">Contact</Link>
          </nav>
          <Link
            to="/signup"
            className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
            data-testid="blog-nav-signup"
          >
            Try free <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pt-16 sm:pt-24 pb-8">
        <div className="max-w-3xl">
          <div className="overline mb-4">THE FREIGHT PILOT BLOG</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            Playbooks, deep dives, and truck-tested tactics.
          </h1>
          <p className="text-neutral-400 mt-6 text-base sm:text-lg leading-relaxed">
            Real ops content for trucking companies — no fluff, no listicles, just the mechanics of running a fleet in 2026.
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-posts-grid">
          {POSTS.map((p) => <PostCard key={p.slug} post={p} />)}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24 border-t border-white/10 text-center">
        <div className="inline-flex items-center gap-2 pill pill-primary mb-4">
          <Sparkle size={12} weight="fill" /> READY TO AUTOMATE?
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
          Stop reading. Start automating.
        </h2>
        <p className="text-neutral-400 mt-4 text-sm sm:text-base max-w-lg mx-auto">
          Try FreightPilot AI free for 7 days. See dispatch drafted by AI on your first load in under 60 seconds.
        </p>
        <Link
          to="/signup"
          data-testid="blog-cta-signup"
          className="btn-primary rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 mt-8"
        >
          Start free trial <ArrowRight size={14} weight="bold" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Freight Pilot AI. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="card-surface overflow-hidden hover-lift flex flex-col group"
      data-testid={`blog-card-${post.slug}`}
    >
      <div className={`aspect-[16/9] bg-gradient-to-br ${post.cover_gradient} border-b border-white/5 relative overflow-hidden`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="text-[10px] uppercase tracking-widest text-white/70 mb-2">{post.tag}</div>
            <div className="font-display text-lg font-semibold text-white line-clamp-3">{post.title}</div>
          </div>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-3 text-[10px] text-neutral-500 uppercase tracking-widest mb-3">
          <span>{new Date(post.published_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</span>
          <span className="flex items-center gap-1">
            <Clock size={11} /> {post.read_min} min read
          </span>
        </div>
        <p className="text-sm text-neutral-400 leading-relaxed flex-1">{post.excerpt}</p>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
          <div className="text-xs text-neutral-500">{post.author}</div>
          <span className="text-xs text-[#FF5A1F] group-hover:text-[#FF7038] inline-flex items-center gap-1 font-medium">
            Read <ArrowRight size={11} weight="bold" />
          </span>
        </div>
      </div>
    </Link>
  );
}
